import { el } from '../state/store.js';
import { closeX } from './popup.js';

/* PICK A FRAME OF A VIDEO — to mark the screen's corners on.

   The calibration step marks four corners on a still. When the reference is a filmed
   video, the still is one frame of it, and which frame matters: the whole screen
   visible, nothing in front of it, as sharp as the video gets. So the person chooses it
   here, with the same slider-and-step controls as the Unwrap page, and the full-size
   frame is what the corners are then marked on.

   Resolves with a canvas of that frame at the video's own size, or null when cancelled. */

/**
 * @param {object} clip  from unwrap/decode.js openClip()
 * @param {object} [opts]
 * @param {string} [opts.title]
 */
export function pickFrame(clip, { title = 'Choose the frame to mark' } = {}) {
  return new Promise(resolve => {
    const wrap = el('div', 'authwrap');
    const card = el('div', 'authcard authcard--wide fpick');
    card.appendChild(el('h2', null, title));
    card.appendChild(el('p', 'authnote',
      'Pick a moment where the whole screen can be seen. You will mark its four corners on this frame, '
      + 'and the video is then used behind the on-site render.'));

    const stage = el('div', 'fpick__stage');
    const canvas = el('canvas', 'fpick__canvas'); canvas.width = 16; canvas.height = 9;
    stage.appendChild(canvas);
    card.appendChild(stage);

    const row = el('div', 'fpick__row');
    const back = el('button', 'btn btn--sm btn--gh', '‹'); back.type = 'button'; back.title = 'Previous frame'; back.setAttribute('aria-label', 'Previous frame');
    const fwd = el('button', 'btn btn--sm btn--gh', '›'); fwd.type = 'button'; fwd.title = 'Next frame'; fwd.setAttribute('aria-label', 'Next frame');
    const slider = el('input'); slider.type = 'range'; slider.className = 'fpick__range';
    slider.min = '0'; slider.max = String(clip.duration); slider.step = String(1 / Math.max(1, clip.fps));
    slider.setAttribute('aria-label', 'Moment in the video');
    const time = el('span', 'fpick__time', '');
    row.append(back, slider, fwd, time);
    card.appendChild(row);

    const info = el('p', 'note', `${clip.name} · ${clip.w} × ${clip.h} · ${+clip.fps.toFixed(2)} fps · ${clip.duration.toFixed(1)} s`);
    card.appendChild(info);

    const btns = el('div', 'authrow authrow--split');
    const cancel = el('button', 'btn btn--gh', 'Cancel'); cancel.type = 'button';
    const use = el('button', 'btn btn--pri', 'Use this frame'); use.type = 'button';
    btns.append(cancel, use);
    card.appendChild(btns);
    wrap.appendChild(card);
    document.body.appendChild(wrap);

    let done = false, token = 0, t = Math.min(0.5, clip.duration / 2);
    const finish = v => { if (done) return; done = true; wrap.remove(); resolve(v); };

    const show = async at => {
      const mine = ++token;
      t = Math.min(Math.max(0, at), clip.duration);
      time.textContent = `${t.toFixed(2)} / ${clip.duration.toFixed(2)} s`;
      const f = await clip.frameAt(t, 960).catch(() => null);
      if (mine !== token || !f) return;                    // a newer request has overtaken this one
      canvas.width = f.canvas.width; canvas.height = f.canvas.height;
      canvas.getContext('2d').drawImage(f.canvas, 0, 0);
    };
    const step = d => { const v = Math.min(clip.duration, Math.max(0, t + d / Math.max(1, clip.fps))); slider.value = String(v); show(v); };
    slider.value = String(t);
    slider.oninput = () => show(parseFloat(slider.value));
    back.onclick = () => step(-1); fwd.onclick = () => step(1);
    cancel.onclick = () => finish(null);
    closeX(card, () => finish(null));
    wrap.onclick = e => { if (e.target === wrap) finish(null); };

    use.onclick = async () => {
      use.disabled = true;
      try {
        const full = await clip.frameAt(t, clip.w);        // the marks go on the full-size frame, not the preview
        finish(full ? full.canvas : null);
      } catch (_) { finish(null); }
    };
    show(t);
  });
}
